"use client";

import qs from "qs";
import React, { Fragment, useEffect, useRef, useState } from "react";
import Link from "next/link";
import escapeHTML from "escape-html";
import { Text } from "slate";
import {
  Category,
  Brand,
  Product,
  Media,
  Size,
  Color,
} from "../../../payload/payload-types";
import { AddToCartButton } from "../../_components/AddToCartButton";
import { Gutter } from "../../_components/Gutter";
import { Price } from "../../_components/Price";
import { useAuth } from "../../_providers/Auth";
import { CustomHR } from "../../_components/HR/CustomHR";
import BrandProducts from "../../_components/BrandProducts";

import classes from "./index.module.scss";
import { Button } from "../../_components/Button/index";
import ReviewsBlock from "./ReviewsBlock/index";
import AllProductReviews from "./AllProductReviews";
import RelatedProducts from "./RelatedProducts/index";
import "react-multi-carousel/lib/styles.css";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Image from "next/image";
import { IoIosHeart } from "react-icons/io";
import { SiWhatsapp } from "react-icons/si";
import { useCart } from "../../_providers/Cart";
import { QuickAddToCart } from "../../_components/QuickAddToCart";
import { useQuickAdd } from "../../_providers/QuickCart";
import { FaRegCircleCheck } from "react-icons/fa6";

export const ProductHero: React.FC<{
  product: Product;
}> = ({ product }) => {
  //console.log("ProductHero", product);
  const {
    id,
    title,
    slug,
    brands,
    gallery,
    detailsCare,
    sizeInfo,
    description,
    categories,
    relatedProducts,
    availableSizes,
    availableColors,
    stock,
    variations,
    meta: { image: metaImage } = {},
  } = product;
  const [savedProduct, setSavedProduct] = useState(false);
  const [isLoadedSavedProduct, setIsLoadedSavedProduct] = useState(null);
  const [color, setColor] = useState<Color>(null);
  const [size, setSize] = useState<Size>(null);
  const [image, setImage] = useState<Media | null>(null);

  const { user } = useAuth();
  const [expanded, setExpanded] = React.useState<string | false>(false);
  const { quickAdd, setQuickAdd } = useQuickAdd();
  const [thumbNails, setThumbNails] = useState([]);
  const [variant, setVariant] = useState({});

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const serialize = (children) =>
    children?.map((node, i) => {
      if (Text.isText(node)) {
        let text = (
          <span dangerouslySetInnerHTML={{ __html: escapeHTML(node.text) }} />
        );

        if (node['bold']) {
          text = <strong key={i}>{text}</strong>;
        }

        if (node['code']) {
          text = <code key={i}>{text}</code>;
        }

        if (node['italic']) {
          text = <em key={i}>{text}</em>;
        }

        if (node.text === "") {
          text = <br />;
        }

        // Handle other leaf types here...

        return <Fragment key={i}>{text}</Fragment>;
      }

      if (!node) {
        return null;
      }

      switch (node.type) {
        case "h1":
          return <h1 key={i}>{serialize(node.children)}</h1>;
        // Iterate through all headings here...
        case "h6":
          return <h6 key={i}>{serialize(node.children)}</h6>;
        case "blockquote":
          return <blockquote key={i}>{serialize(node.children)}</blockquote>;
        case "ul":
          return <ul key={i}>{serialize(node.children)}</ul>;
        case "ol":
          return <ol key={i}>{serialize(node.children)}</ol>;
        case "li":
          return <li key={i}>{serialize(node.children)}</li>;
        case "link":
          return (
            <a href={escapeHTML(node.url)} key={i}>
              {serialize(node.children)}
            </a>
          );

        default:
          return <section key={i}>{serialize(node.children)}</section>;
      }
    });

  useEffect(() => {
    checkProductSave();
  }, [savedProduct, user, isLoadedSavedProduct, color, size, image]);

  useEffect(() => {
    let newThumbnails = [];

    if (gallery?.[0]?.image) {
      newThumbnails.push(gallery[0].image);
    }

    if (variations.length > 0) {
      const firstUrls = variations
        .map((variation) => {
          return variation.variationGallery?.[0]?.image;
        })
        .filter(Boolean);

      newThumbnails = [...newThumbnails, ...firstUrls];
    }
    setThumbNails(newThumbnails);
  }, [variations, gallery]);

  const handleWishList = (productId) => {
    if (savedProduct) {
      setSavedProduct(false);
      removeProduct(productId);
    } else {
      setSavedProduct(true);
      saveProduct(productId);
    }
  };

  const onChangeSize = async (event) => {
    const value = event.target.value;
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_SERVER_URL}/api/sizes/${value}`
    );
    const data = await res.json();
    setSize(data);
  };

  const handleSizeClick = async (value) => {
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_SERVER_URL}/api/sizes/${value}`
    );
    const data = await res.json();
    setSize(data);
  };

  const onChangeColor = async (event) => {
    const value = event.target.value;
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_SERVER_URL}/api/colors/${value}`
    );
    const data = await res.json();
    setColor(data);
  };

  const handleColorClick = async (value) => {
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_SERVER_URL}/api/colors/${value}`
    );
    const data = await res.json();
    setColor(data);
  };

  const handleVariationClick = async (url: string) => {
    const selectedImage = thumbNails.find((img) => img.url === url);
    if (selectedImage) {
      setImage(selectedImage);
    }
  };

  const checkProductSave = async () => {
    if (user) {
      const stringifiedQuery = qs.stringify(
        {
          where: {
            product: {
              equals: `${product?.id}`,
            },
            user: {
              equals: `${user?.id}`,
            },
          },
        },
        { addQueryPrefix: true }
      );

      if (isLoadedSavedProduct == null) {
        try {
          const req = await fetch(
            `${process.env.NEXT_PUBLIC_SERVER_URL}/api/wishlist/${stringifiedQuery}`
          );
          const data = await req.json();
          if (data.docs.length > 0) {
            setSavedProduct(true);
            setIsLoadedSavedProduct(product?.id);
          }
          //setLoaded(true);
        } catch (err) {
          console.log(err);
        }
      }
    }
  };

  const saveProduct = async (productId) => {
    try {
      const req = await fetch(
        `${process.env.NEXT_PUBLIC_SERVER_URL}/api/wishlist`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user: user?.id,
            product: productId,
          }),
        }
      );
      const data = await req.json();
    } catch (err) {
      console.log(err);
    }
  };

  const removeProduct = async (productId) => {
    const stringifiedQuery = qs.stringify(
      {
        where: {
          product: {
            equals: `${productId}`,
          },
          user: `${user?.id}`,
        },
      },
      { addQueryPrefix: true }
    );

    try {
      const req = await fetch(
        `${process.env.NEXT_PUBLIC_SERVER_URL}/api/wishlist/${stringifiedQuery}`,
        {
          method: "DELETE",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const data = await req.json();
    } catch (err) {
      console.log(err);
    }
  };


  useEffect(() => {
    if (!image?.url) return;

    const targetUrl = image.url;

    const matchedVariation = variations.find((variation) =>
        variation.variationGallery.some(
            (item) => item?.image['url'] === targetUrl
        )
    );

    setVariant(matchedVariation);
  }, [image?.url, variations]);

  const displayDynamicGallery = () => {
    //   const targetUrl = image?.url;
    //
    // const matchedVariation = variations.find((variation) =>
    //     variation.variationGallery.some(
    //         (item) => item?.image?.url === targetUrl
    //     )
    // );
    //
    // setVariant(matchedVariation);

    let variableGallery = [];
    if (variant && typeof variant === "object" && !Array.isArray(variant) && Object.keys(variant).length > 0 ) {
      variableGallery = variant['variationGallery'];
    }else{
      variableGallery = gallery;
    }

    {!variableGallery && <div className={classes.placeholder}>No image</div>}
    return variableGallery?.map((gal, index) => {
      const mediaGallery = gal?.image as Media;
      return (
          <Link
              key={index}
              href={"/media/" + mediaGallery.filename}
              target="_blank"
              className={classes.card}
          >
            <Image
                className={classes.image}
                src={`${mediaGallery.url}`}
                alt={`${mediaGallery.alt}`}
                blurDataURL={`${mediaGallery.url}`}
                placeholder={"blur"}
                loading="eager"
                fill={true}
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            />
          </Link>
      );
    })
  }

  return (
    <Gutter className={classes.productHero} left={false} right={false}>
      <div className={classes.upperLayoutProduct}>
        <div className={classes.mediaWrapper}>
          {displayDynamicGallery()}
        </div>

        <div className={classes.detailsRelatedProducts}>
          <div className={classes.details}>
            <h1 className={classes.title}>{title}</h1>
            <span className={classes.stock}>
              {stock ? (
                <span className={classes.stock}>In Stock</span>
              ) : (
                <span className={classes.outStock}>Out of Stock</span>
              )}
            </span>
            <div className={classes.categoryWrapper}>
              <div className={classes.categories}>
                {categories?.map((category, index) => {
                  const { title: categoryTitle } = category as Category;

                  const titleToUse = categoryTitle || "Generic";
                  const isLast = index === categories.length - 1;

                  return (
                    <span key={index} className={classes.category}>
                      {titleToUse} {!isLast && <Fragment>, &nbsp;</Fragment>}
                      {/* <span className={classes.separator}></span> */}
                    </span>
                  );
                })}
              </div>
              <Price product={product} button={false} />
            </div>
            {/* <div className={classes.description}>
              <h6>Description</h6>
              <p>{description}</p>
            </div> */}
            {availableSizes.length != 0 && (
              <>
                <div className={classes.displayWeb}>
                  <select onChange={onChangeSize} className={classes.select}>
                    <option value="">--Size--</option>
                    {availableSizes?.map((size: Size, index) => {
                      return (
                        <option key={index} value={size?.id}>
                          {size?.name}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className={classes.displayMobile}>
                  <span>Sizes:</span>
                  {availableSizes?.map((sizeInner: Size, index) => {
                    return (
                      <span
                        key={index}
                        className={
                          size?.id === sizeInner?.id
                            ? classes.sizeOptionSelected
                            : classes.sizeOption
                        }
                        onClick={() => handleSizeClick(sizeInner.id)}
                      >
                        {sizeInner.code}
                      </span>
                    );
                  })}
                </div>
              </>
            )}

            {availableColors.length != 0 && (
              <>
                <div className={classes.displayWeb}>
                  <select onChange={onChangeColor} className={classes.select}>
                    <option value="">--Color--</option>
                    {availableColors?.map((color: Color, index) => {
                      return (
                        <option key={index} value={color?.id}>
                          {color?.name}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className={classes.displayMobile}>
                  <span className={classes.titleOption}>Colors:</span>
                  {availableColors?.map((colorsInner: Color, index) => {
                    return (
                      <span
                        key={index}
                        className={
                          color?.id === colorsInner?.id
                            ? classes.colorOptionSelected
                            : classes.sizeOption
                        }
                        onClick={() => handleColorClick(colorsInner?.id)}
                        style={{ background: colorsInner?.code }}
                      ></span>
                    );
                  })}
                </div>
              </>
            )}

            {thumbNails.length > 1 && (
              <div className={classes.displayVariations}>
                <span className={classes.titleOption}></span>
                {thumbNails?.map((innerImages: Media, index) => {
                  const isSelected = image?.url === innerImages?.url;
                  return (
                    <span
                      key={index}
                      className={classes.spanVariations}
                      onClick={() => handleVariationClick(innerImages?.url)}
                    >
                      <div className={classes.imageVariationWrapper}>
                        <Image
                          className={classes.imageVariations}
                          src={`${innerImages.url}`}
                          alt={`${innerImages.alt}`}
                          blurDataURL={`${innerImages.url}`}
                          placeholder={"blur"}
                          loading="eager"
                          fill={true}
                        />
                        {isSelected && (
                          <div className={classes.checkIcon}>
                            <FaRegCircleCheck size="20" color="#fff" />
                          </div>
                        )}
                      </div>
                    </span>
                  );
                })}
              </div>
            )}

            <AddToCartButton
              product={product}
              className={classes.addToCartButton}
              color={color}
              size={size}
              variation={variant}
            />
            {user ? (
              <Button
                onClick={() => handleWishList(product?.id)}
                className={classes.wishlistBtn}
                label={
                  savedProduct ? (
                    <>
                      Remove from Wishlist &nbsp; &nbsp;{" "}
                      <IoIosHeart size={7} style={{ color: "#D90707" }} />
                    </>
                  ) : (
                    <>
                      Add to Wishlist &nbsp; &nbsp;{" "}
                      <IoIosHeart size={7} style={{ color: "#000" }} />
                    </>
                  )
                }
                appearance="secondary"
              />
            ) : (
              <Button
                href={"/login"}
                className={classes.wishlistBtn}
                label={
                  <>
                    Add to Wishlist &nbsp; &nbsp;{" "}
                    <IoIosHeart size={7} style={{ color: "#000" }} />
                  </>
                }
                appearance="secondary"
              />
            )}

            <div className={classes.accordionWrapper}>
              <Accordion
                // expanded={expanded === "panel1"}
                // onChange={handleChange("panel1")}
                defaultExpanded
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  <Typography
                    component="span"
                    sx={{ width: "33%", flexShrink: 0 }}
                  >
                    Details & Care
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <div>{detailsCare && serialize(detailsCare)}</div>
                </AccordionDetails>
              </Accordion>
              <Accordion
                expanded={expanded === "panel2"}
                onChange={handleChange("panel2")}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel2bh-content"
                  id="panel2bh-header"
                >
                  <Typography
                    component="span"
                    sx={{ width: "33%", flexShrink: 0 }}
                  >
                    Size & Info
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <div>{sizeInfo && serialize(sizeInfo)}</div>
                </AccordionDetails>
              </Accordion>
              <Accordion
                expanded={expanded === "panel3"}
                onChange={handleChange("panel3")}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel3bh-content"
                  id="panel3bh-header"
                >
                  <Typography
                    component="span"
                    sx={{ width: "33%", flexShrink: 0 }}
                  >
                    Shipping & returns
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>
                    We ship immediately to you after payment is confirmed.
                    Shipping might take between 5 to 7 working days. We also
                    have a 7 days return policy on all items.
                  </Typography>
                </AccordionDetails>
              </Accordion>
              <Accordion
                expanded={expanded === "panel4"}
                onChange={handleChange("panel4")}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel4bh-content"
                  id="panel4bh-header"
                >
                  <Typography
                    component="span"
                    sx={{ width: "33%", flexShrink: 0 }}
                  >
                    Reviews
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <ReviewsBlock productId={product?.id} />
                </AccordionDetails>
              </Accordion>
            </div>
          </div>
          <div className={classes.relatedProducts}>
            <RelatedProducts relatedProducts={relatedProducts} />
          </div>
        </div>
      </div>

      <div className={classes.relatedProductsMobile}>
        <RelatedProducts relatedProducts={relatedProducts} />
      </div>

      <div className={classes.allReviewsBlock}>
        <AllProductReviews productId={product?.id} />
      </div>
      <div className={classes.brandProductsBlock}>
        <CustomHR />
        <BrandProducts brand={brands} />
      </div>
      <Link
        className={classes.whatsappPosition}
        target="_blank"
        href={
          `https://api.whatsapp.com/send?phone=+2348085941102&text=` +
          encodeURIComponent(
            `I need help with this product ${process.env.NEXT_PUBLIC_SERVER_URL}/products/${slug}`
          )
        }
        aria-label={"Chat us on whatsapp"}
      >
        Chat with us <SiWhatsapp size={40} color={"#25D366"} />
      </Link>
      {quickAdd && <QuickAddToCart product={product} />}
    </Gutter>
  );
};
